import React, { useState, useEffect, useContext } from "react";
import { useQuery } from "react-apollo-hooks";
import { NavLink } from "react-router-dom";
import { graphql, compose } from "react-apollo";
import { ButtonToolbar, OverlayTrigger, Tooltip } from "react-bootstrap";
import {
  FaPen,
  FaCheck,
  FaTrash,
  FaCalendarDay,
  FaEnvelope,
  FaPaperclip,
  FaSms,
  FaClock,
  FaListAlt,
  FaPhone,
  FaClone,
  FaBan,
  FaCalendarCheck,
  FaVideo,
  FaHome,
  FaEye,
  FaWalking,
  FaSave,
  FaStar,
} from "react-icons/fa";
import { BsEnvelopeArrowUpFill } from "react-icons/bs";
import { Auth } from "../../rbac/rbac";
import routes_constants from "../layout/routes";
import Dialog from "../../components/dialog/dialog";
import CustomButton from "../../components/custom-button";
import {
  formatActivityDate,
  formatDate,
  emailPreviewBody,
  truncate,
  addHttpToUrl,
  splitIntoFields,
  InactivityIcon,
  formatCallDurationsWithSeconds,
} from "../../utils/helpers";
import { getFloorPlanActivityUrl } from "../../utils/floorPlanActivityUrl";
import {
  LEAD,
  PROSPECT,
  HOMEOWNER,
  ROLE_OSC,
  REALTOR,
  MARKETING_ONLY,
  AppointmentPurpose,
} from "../../utils/constants";
import { FETCH_CALL_RECORDING } from "../../graphql/queries";
import {
  DELETE_LEAD_FOLLOW_UP,
  MARK_LEAD_FOLLOW_UP_DONE,
  DELETE_LEAD_PHONE_CALL,
  DELETE_LEAD_TRAFFIC,
  DELETE_HOMEOWNER_APPOINTMENT,
  MARK_HOMEOWNER_APPOINTMENT_DONE,
  DELETE_HOMEOWNER_PHONE_CALL,
  DELETE_HOMEOWNER_TRAFFIC,
  DELETE_PROSPECT_PHONE_CALL,
  DELETE_PROSPECT_FOLLOW_UP,
  DELETE_PROSPECT_APPOINTMENT,
  DELETE_PROSPECT_TRAFFIC,
  MARK_PROSPECT_APPOINTMENT_DONE,
  MARK_PROSPECT_FOLLOW_UP_DONE,
  MARK_LEAD_APPOINTMENT_DONE,
  DELETE_LEAD_APPOINTMENT,
  DELETE_HOMEOWNER_FOLLOW_UP,
  MARK_HOMEOWNER_FOLLOW_UP_DONE,
  MARK_REALTOR_APPOINTMENT_DONE,
  MARK_REALTOR_FOLLOWUP_DONE,
  RESEND_PROSPECT_APPOINTMENT_CONFIRMATION,
  RESEND_LEAD_APPOINTMENT_CONFIRMATION,
  RESEND_REALTOR_APPOINTMENT_CONFIRMATION,
} from "../../graphql/mutations";
import massMailLogo from "../../images/icon-massEmailColored.svg";
import AppointmentDeleteDialogBox from "../../components/dialog/appointment-delete-dialog-box";
import DELETE_REALTOR_APPOINTMENT from "../../graphql/mutations/activity/realtor/delete-appointment";
import DELETE_REALTOR_FOLLOW_UP from "../../graphql/mutations/activity/realtor/delete-follow-up";

/**
 * This component is used to render the calendar activities popovers
 * @param {object} props component props
 * @param {object} props.item contains all the information related to the activities
 * @param {string} props.type type of the customer
 * @param {function} props.markDoneLeadFollowUp
 * @param {function} props.deleteLeadFollowUp
 * @param {function} props.deleteLeadPhoneCall
 * @param {function} props.markDoneProspectAppointment
 * @param {function} props.deleteProspectAppointment
 * @param {function} props.markDoneProspectFollowUp
 * @param {function} props.deleteProspectFollowUp
 * @param {function} props.deleteProspectPhoneCall
 * @param {function} props.deleteHomeownerPhoneCall
 * @param {function} props.handleDoneClick
 * @param {function} props.markDoneRealtorAppointment
 * @param {function} props.markDoneRealtorFollowup
 * @param {function} props.onSubmit
 */
const Popover = (props) => {
  const { user, is_manager, is_divmgr } = useContext(Auth);
  const isOsc = user.role === ROLE_OSC || user.secondary_role === ROLE_OSC;
  const canEditLeadAppointment = isOsc || is_manager() || is_divmgr();
  const isAppointment = props?.item?.event?.__typename?.toLowerCase().includes("appointment");
  const isAppointmentTimePassed =
    isAppointment &&
    props.item.event.start_datetime &&
    new Date(props.item.event.start_datetime) < new Date();
  const isFollowUp = props?.item?.event?.__typename?.toLowerCase().includes("follow_up");
  const isPhoneCallOrText = ["PhoneCall", "Text"].includes(props?.item?.event?.__typename)
  const isTraffic = props?.item?.event?.__typename?.toLowerCase().includes("traffic");
  const isFloorPlanActivity = ["SaveFloorPlanActivity", "FavoriteFloorPlanActivity"].includes(
    props?.item?.event?.__typename
  );
  const floorPlanUrl = isFloorPlanActivity
    ? getFloorPlanActivityUrl(props.item.event)
    : "";
  const isMarketingOnly = props.type === MARKETING_ONLY
  const isDashboardCalendar = props.id === "dashboard-calendar";

  const [state, setState] = useState({
    showDeleteConfirmation: false,
    id: [],
    showMarkDoneConfirmation: false,
    showEmailPreview: false,
    recordingUrl: "",
    deleteNote: null,
    sendEmail: true
  });

  const handleAppointmentDeleteInputChange = ({ note, isSendEmailChecked }) => {
    setState((prevState) => ({
      ...prevState,
      deleteNote: note !== undefined ? note : prevState.deleteNote,
      sendEmail: isSendEmailChecked !== undefined ? isSendEmailChecked : prevState.sendEmail,
    }));
  };

  const { refetch: getPhoneCallRecording } = useQuery(FETCH_CALL_RECORDING, { skip: true });

  const handleNameClick = () => {
    const { prospect_id, lead_id, realtor_id, homeowner_id, marketing_only_id } = props.item?.event;

    let path = null;
    if (prospect_id) {
      path = `${routes_constants.PROSPECT_DETAIL}/${prospect_id}`;
    } else if (lead_id) {
      path = `${routes_constants.LEAD_DETAIL}/${lead_id}`;
    } else if (realtor_id) {
      path = `${routes_constants.REALTOR_DETAIL}/${realtor_id}`;
    } else if (homeowner_id) {
      path = `${routes_constants.HOME_OWNER_DETAIL}/${homeowner_id}`;
    } else if (marketing_only_id) {
      path = `${routes_constants.MARKETING_ONLY_DETAIL}/${marketing_only_id}`;
    }

    if (path) {
      window.location.pathname = path;
    }
  };

  /**
   * this function is used to show/hide delete confirmation dialog
   * @method
   * @param {object} item Contains all the information regarding the activity.
   */
  const handleDeleteClick = (item) => {
    setState({
      ...state,
      showDeleteConfirmation: !state.showDeleteConfirmation,
      id: [item.id],
      recordingUrl: "",
    });
  };

  /**
   * this function is used to hide the dialogs
   * @method
   */
  const hideDialog = () => {
    setState({
      ...state,
      showDeleteConfirmation: false,
      showMarkDoneConfirmation: false,
      id: null,
      deleteNote: null,
      sendEmail: true
    });
  };

  /**
   * this function is used to fetch phone/voicemail recording from backend and open it in media player
   * @method
   */
  const handleFetchRecording = () => {
    getPhoneCallRecording({
      phone_call_id: props.item.event.phone_call_id,
    }).then((res) => {
      if (res.data.getPhoneCallRecording.code === 200) {
        setState({
          ...state,
          recordingUrl: res.data.getPhoneCallRecording.message,
        });
      }
    });
  };

  /**
   * this function is used to show/hide mark done confirmation dialog
   * @method
   * @param {object} item Contains all the information regarding the activity.
   */
  const handleMarkDoneClick = (item) => {
    setState({
      ...state,
      showMarkDoneConfirmation: !state.showMarkDoneConfirmation,
      id: item.id,
    });
  };

  /**
   * this function is used to mark an activity done
   * @method
   */
  const handleMarkDoneConfirmation = () => {
    // Map for appointment mutations by type
    const appointmentMutations = {
      [PROSPECT]: {
        fn: props.markDoneProspectAppointment,
        key: "markDoneProspectAppointment",
      },
      [HOMEOWNER]: {
        fn: props.markDoneHomeownerAppointment,
        key: "markDoneHomeownerAppointment",
      },
      [LEAD]: {
        fn: props.markDoneLeadAppointment,
        key: "markDoneLeadAppointment",
      },
      [REALTOR]: {
        fn: props.markDoneRealtorAppointment,
        key: "markDoneRealtorAppointment",
      },
    };

    // Handle Appointments
    if (isAppointment) {
      // Determine type
      let typeKey = PROSPECT;
      if (props.type === HOMEOWNER || props.item.event.homeowner_id) {
        typeKey = HOMEOWNER;
      } else if (props.type === LEAD || props.item.event.lead_id) {
        typeKey = LEAD;
      } else if (props.type === REALTOR || props.item.event.realtor_id) {
        typeKey = REALTOR;
      }

      const mutation = appointmentMutations[typeKey];
      if (mutation) {
        mutation
          .fn({
            variables: { input: { appointment_ids: [state.id], done: true } },
          })
          .then((res) => {
            if (res.data[mutation.key].code === 200) {
              props.handleDoneClick();
              props.onSubmit(res.data[mutation.key].message);
            }
          });
      }
      return;
    }

    // Map for follow-up mutations by typename
    const followUpMutations = {
      Prospect_Follow_Up: {
        fn: props.markDoneProspectFollowUp,
        key: "markDoneProspectFollowUp",
      },
      Lead_Follow_Up: {
        fn: props.markDoneLeadFollowUp,
        key: "markDoneLeadFollowUp",
      },
      Homeowner_Follow_Up: {
        fn: props.markDoneHomeownerFollowUp,
        key: "markDoneHomeownerFollowUp",
      },
      Realtor_Follow_Up: {
        fn: props.markDoneRealtorFollowup,
        key: "markDoneRealtorFollowup",
      },
    };

    // Handle Follow-Ups
    const followUpKey = Object.keys(followUpMutations).find(
      (key) => key === props?.item?.event?.__typename
    );

    if (followUpKey) {
      const mutation = followUpMutations[followUpKey];
      mutation
        .fn({
          variables: { input: { follow_up_ids: [state.id], done: true } },
        })
        .then((res) => {
          if (res.data[mutation.key].code === 200) {
            props.handleDoneClick();
            props.onSubmit(res.data[mutation.key].message);
          }
        });
    }
  };

  const handleResendConfirmationEmail = () => {
    const appointmentResendMutations = {
      [PROSPECT]: {
        fn: props.resendProspectAppointmentConfirmation,
        key: "resendProspectAppointmentConfirmation",
      },
      [LEAD]: {
        fn: props.resendLeadAppointmentConfirmation,
        key: "resendLeadAppointmentConfirmation",
      },
      [REALTOR]: {
        fn: props.resendRealtorAppointmentConfirmation,
        key: "resendRealtorAppointmentConfirmation",
      },
    };

    let typeKey = PROSPECT;
    if (props.type === LEAD || props.item.event.lead_id) {
      typeKey = LEAD;
    } else if (props.type === REALTOR || props.item.event.realtor_id) {
      typeKey = REALTOR;
    }

    const mutation = appointmentResendMutations[typeKey];
    if (!mutation?.fn) return;

    mutation
      .fn({
        variables: {
          appointment_id: props.item.event.id,
        },
      })
      .then((res) => {
        const response = res?.data?.[mutation.key];
        if (response?.message) {
          props.onSubmit(response.message);
        }
      });
  };

  /**
   * this function is used when delete confirmation has been made and deletes the activity
   * @method
   */
  const handleDeleteConfirmation = () => {
    // Map for appointment delete mutations by type
    const appointmentDeleteMutations = {
      [PROSPECT]: {
        fn: props.deleteProspectAppointment,
        key: "deleteProspectAppointment",
      },
      [HOMEOWNER]: {
        fn: props.deleteHomeownerAppointment,
        key: "deleteHomeownerAppointment",
      },
      [LEAD]: {
        fn: props.deleteLeadAppointment,
        key: "deleteLeadAppointment",
      },
      [REALTOR]: {
        fn: props.deleteRealtorAppointment,
        key: "deleteRealtorAppointment",
      },
    };

    // Handle Appointments
    if (isAppointment) {
      // Determine type
      let typeKey = PROSPECT;
      if (props.type === HOMEOWNER || props.item.event.homeowner_id) {
        typeKey = HOMEOWNER;
      } else if (props.type === LEAD || props.item.event.lead_id) {
        typeKey = LEAD;
      } else if (props.type === REALTOR || props.item.event.realtor_id) {
        typeKey = REALTOR;
      }

      const mutation = appointmentDeleteMutations[typeKey];
      if (mutation) {
        mutation
          .fn({
            variables: {
              input: {
                appointment_ids: state.id,
                note: state.deleteNote
              },
              sendEmail: state.sendEmail
            },
          })
          .then((res) => {
            if (res.data[mutation.key].code === 200) {
              setState({
                ...state,
                showDeleteConfirmation: !state.showDeleteConfirmation,
                deleteNote: null,
                sendEmail: true
              });
              props.onSubmit(res.data[mutation.key].message);
            }
          });
      }
      return;
    }

    // Map for phone call delete mutations by type
    const phoneCallDeleteMutations = {
      [PROSPECT]: {
        fn: props.deleteProspectPhoneCall,
        key: "deleteProspectPhoneCall",
      },
      [LEAD]: {
        fn: props.deleteLeadPhoneCall,
        key: "deleteLeadPhoneCall",
      },
      [HOMEOWNER]: {
        fn: props.deleteHomeownerPhoneCall,
        key: "deleteHomeownerPhoneCall",
      },
    };

    // Handle PhoneCall or Text
    if (isPhoneCallOrText) {
      let typeKey = PROSPECT;
      if (props.type === HOMEOWNER || props.item.event.homeowner_id) {
        typeKey = HOMEOWNER;
      } else if (props.type === LEAD || props.item.event.lead_id) {
        typeKey = LEAD;
      }
      const mutation = phoneCallDeleteMutations[typeKey];
      if (mutation) {
        mutation
          .fn({
            variables: {
              input: props.item.event.phone_call_id,
            },
          })
          .then((res) => {
            if (res.data[mutation.key].code === 200) {
              setState({
                ...state,
                showDeleteConfirmation: !state.showDeleteConfirmation,
              });
              props.onSubmit(res.data[mutation.key].message);
            }
          });
      }
      return;
    }

    // Map for follow-up delete mutations by typename
    const followUpDeleteMutations = {
      Prospect_Follow_Up: {
        fn: props.deleteProspectFollowUp,
        key: "deleteProspectFollowUp",
      },
      Lead_Follow_Up: {
        fn: props.deleteLeadFollowUp,
        key: "deleteLeadFollowUp",
      },
      Homeowner_Follow_Up: {
        fn: props.deleteHomeownerFollowUp,
        key: "deleteHomeownerFollowUp",
      },
      Realtor_Follow_Up: {
        fn: props.deleteRealtorFollowUp,
        key: "deleteRealtorFollowUp",
      },
    };

    // Handle Follow-Ups
    const followUpKey = Object.keys(followUpDeleteMutations).find(
      (key) => key === props?.item?.event?.__typename
    );

    if (followUpKey) {
      const mutation = followUpDeleteMutations[followUpKey];
      mutation
        .fn({
          variables: { input: { follow_up_ids: state.id } },
        })
        .then((res) => {
          if (res.data[mutation.key].code === 200) {
            setState({
              ...state,
              showDeleteConfirmation: !state.showDeleteConfirmation,
            });
            props.onSubmit(res.data[mutation.key].message);
          }
        });
      return;
    }

    // Map for traffic delete mutations by typename
    const trafficDeleteMutations = {
      [PROSPECT]: {
        fn: props.deleteProspectTraffic,
        key: "deleteProspectTraffic",
      },
      [LEAD]: {
        fn: props.deleteLeadTraffic,
        key: "deleteLeadTraffic",
      },
      [HOMEOWNER]: {
        fn: props.deleteHomeownerTraffic,
        key: "deleteHomeownerTraffic",
      },
    };
    if(isTraffic) {
      let trafficKey = PROSPECT;
      if (props.type === HOMEOWNER || props.item.event.homeowner_id) {
        trafficKey = HOMEOWNER;
      } else if (props.type === LEAD || props.item.event.lead_id) {
        trafficKey = LEAD;
      }
      if (trafficKey) {
        const mutation = trafficDeleteMutations[trafficKey];
        mutation
          .fn({
            variables: { input: { traffic_ids: state.id } },
          })
          .then((res) => {
            if (res.data[mutation.key].code === 200) {
              setState({
                ...state,
                showDeleteConfirmation: !state.showDeleteConfirmation,
              });
              props.onSubmit(res.data[mutation.key].message);
            }
          });
      }
    }
  };

  useEffect(() => {
    if (
      props.item.event.__typename === "PhoneCall" &&
      props.item.event.recording
    ) {
      handleFetchRecording();
    }
  }, []);

  let date;
  let endDate;
  let newDate;
  let csmNames;

  if (isAppointment) {
    date = new Date(props.item.event.start_datetime);
    endDate = new Date(props.item.event.end_datetime);
    newDate = formatActivityDate(date) + " - " + formatActivityDate(endDate);
  } else if (isFollowUp) {
    date = new Date(props.item.event.follow_up_datetime);
    newDate = formatActivityDate(date);
  } else if (isPhoneCallOrText) {
    date = new Date(props.item.event.call_datetime);
    newDate = formatActivityDate(date);
  } else if (isTraffic) {
    const actualDate = props.item.event.traffic_date || props.item.event.created_at;
    date = new Date(actualDate);
    newDate = formatActivityDate(date);
  } else if (isFloorPlanActivity) {
    date = new Date(props.item.event.activity_datetime || props.item.event.created_at);
    newDate = formatActivityDate(date);
  } else if (props.item.event.__typename === "Email") {
    date = new Date(props.item.event.date_time);
    newDate = formatActivityDate(date);
    csmNames = props.item.event.csms.map(
      (csm) => csm.first_name + " " + csm.last_name
    );
  } else if (props.item.event.__typename === "MailchimpEmailType") {
    date = new Date(props.item.event.datetime);
    newDate = formatActivityDate(date);
  } else if (props.item.event.__typename === "Prospect_Walk_In") {
    date = new Date(props.item.event.created_at);
    newDate = formatActivityDate(date);
  }

  // Helper function to get the detail path
  const getDetailPath = (event) => {
    if (event.prospect_id) {
      return `${routes_constants.PROSPECT_DETAIL}/${event.prospect_id}`;
    } else if (event.lead_id) {
      return `${routes_constants.LEAD_DETAIL}/${event.lead_id}`;
    } else if (event.homeowner_id) {
      return `${routes_constants.HOME_OWNER_DETAIL}/${event.homeowner_id}`;
    } else if (event.realtor_id) {
      return `${routes_constants.REALTOR_DETAIL}/${event.realtor_id}`;
    } else if (event.marketing_only_id) {
      return `${routes_constants.MARKETING_ONLY_DETAIL}/${event.marketing_only_id}`;
    }

    return null;
  };

  return (
    <div className="popover-group-events-calendar">
      <Dialog
        show={state.showDeleteConfirmation}
        onHide={hideDialog}
        title="Delete Confirmation"
        body={
          isAppointment ? (
            <AppointmentDeleteDialogBox
              handleAppointmentDeleteInputChange={
                handleAppointmentDeleteInputChange
              }
            />
          ) : (
            <p>Are you sure you want to delete?</p>
          )
        }
        click={handleDeleteConfirmation}
        disabled={isAppointment && !state?.deleteNote}
        clickname="YES"
        closename="NO"
      />

      <Dialog
        show={state.showMarkDoneConfirmation}
        onHide={hideDialog}
        title="Mark Done Confirmation"
        body="Are you sure you want to mark the record as Done?"
        click={handleMarkDoneConfirmation}
        clickname="YES"
        closename="NO"
      />
      <Dialog
        backdrop={true}
        size="email-event"
        show={state.showEmailPreview}
        onHide={() => {
          setState({ ...state, showEmailPreview: false });
        }}
        title="Email"
        noHeader={true}
        body={emailPreviewBody(props.item.event, newDate, csmNames, () =>
          setState({ ...state, showEmailPreview: false })
        )}
      />
      <div className="d-flex flex-column popover_Title">
        <div className="d-flex justify-content-between">
          <div>
            {isAppointment ? (
              <div
                className="popover_Title_small"
                style={{
                  color: `${
                    props.item.event.__typename === "Realtor_Appointment"
                      ? "rgba(265, 165, 0)"
                      : props.item.event.__typename === "Homeowner_Appointment" && isDashboardCalendar
                        ? "rgb(138, 138, 138)"
                        : "rgb(146, 175, 0)"
                  }`,
                }}
              >
                <FaCalendarCheck />{" "}
                {props.item.event.__typename === "Realtor_Appointment"
                  ? "Realtor Appointment"
                  : props.item.event.__typename === "Homeowner_Appointment" && isDashboardCalendar
                    ? "Homeowner Appointment"
                    : props.item.event.__typename}
              </div>
            ) : isTraffic ? (
              <div
                className="popover_Title_small"
                style={{ color: "rgb(3, 255, 66, 0.3)" }}
              >
                <FaWalking size={12} /> {props.item.event.__typename}
              </div>
            ) : props.item.event.__typename === "PhoneCall" ? (
              <div
                className="popover_Title_small"
                style={{ color: "rgb(80, 227, 194, 1)" }}
              >
                <FaPhone size={12} /> {props.item.event.__typename}
              </div>
            ) : props.item.event.__typename === "Text" ? (
              <div
                className="popover_Title_small"
                style={{ color: "rgb(87, 71, 49, 1)" }}
              >
                <FaSms /> Text
              </div>
            ) : props.item.event.__typename === "Email" &&
              props.item.event.isIndividual ? (
              <div
                className="popover_Title_small"
                style={{ color: "rgb(255, 122, 0, 1)" }}
              >
                <FaEnvelope /> {props.item.event.__typename}
              </div>
            ) : props.item.event.__typename === "Email" ? (
              <div
                className="popover_Title_small"
                style={{ color: "rgb(148, 0, 255, 1)" }}
              >
                <i>
                  <img
                    src={massMailLogo}
                    style={{ fill: "red" }}
                    className="mr-1"
                    alt=""
                  />
                </i>{" "}
                Mass Email
              </div>
            ) : props.item.event.__typename === "MailchimpEmailType" ? (
              <div
                className="popover_Title_small"
                style={{ color: "rgb(208, 2, 27, 1)" }}
              >
                <FaEnvelope /> EBomb
              </div>
            ) : props.item.event.__typename === "CustomerFormSubmission" ? (
              <div
                className="popover_Title_small"
                style={{ color: "rgb(255, 94, 94, 1)" }}
              >
                <FaListAlt /> Web Form
              </div>
            ) : props.item.event.__typename === "Prospect_Walk_In" ? (
              <div
                className="popover_Title_small"
                style={{ color: "rgb(168, 66, 100, 1)" }}
              >
                <FaHome /> Walk-In
              </div>
            ) : isFloorPlanActivity ? (
              <div
                className="popover_Title_small"
                style={{
                  color:
                    props.item.event.__typename === "FavoriteFloorPlanActivity"
                      ? "rgb(136, 14, 79)"
                      : "rgb(27, 94, 32)",
                }}
              >
                {props.item.event.__typename === "FavoriteFloorPlanActivity" ? (
                  <FaStar size={12} />
                ) : (
                  <FaSave size={12} />
                )}{" "}
                {props.item.event.typeTitle}
              </div>
            ) : (
              <div
                className="popover_Title_small"
                style={{
                  color: `${
                    props.item.event.__typename === "Realtor_Follow_Up"
                      ? "rgba(212, 42, 150)"
                      : "rgb(76, 126, 249)"
                  }`,
                }}
              >
                <FaCalendarDay />
                {props.item.event.__typename === "Realtor_Follow_Up"
                  ? "Realtor Follow-Up"
                  : "Follow-Up"}
              </div>
            )}
          </div>

          {(isPhoneCallOrText || isAppointment || isFollowUp || isTraffic)
            && !isMarketingOnly ? (
            <div className="d-flex flex-row">
              {props.id === "dashboard-calendar" && (
                <NavLink
                  to={{
                    pathname: getDetailPath(props.item.event),
                    id:
                      props.item.event.prospect_id ||
                      props.item.event.lead_id ||
                      props.item.event.homeowner_id ||
                      props.item.event.realtor_id ||
                      props.item.event.marketing_only_id,
                  }}
                  className="text-muted"
                >
                  <OverlayTrigger
                    placement="top"
                    overlay={<Tooltip>Show Details</Tooltip>}
                  >
                    <CustomButton
                      size="sm"
                      color="dark"
                      btnIcon={<FaEye size={14} />}
                      className="popover-icon-btn btn-icon"
                    />
                  </OverlayTrigger>
                </NavLink>
              )}
              {(isAppointment || isFollowUp) && !props.item.event.done && (
                <CustomButton
                  size="sm"
                  color="dark"
                  btnIcon={<FaCheck size={14} />}
                  onClick={() => handleMarkDoneClick(props.item.event)}
                  className="popover-icon-btn btn-icon"
                />
              )}{" "}
              {props.id !== "dashboard-calendar" &&
                props.type !== "webform" && (
                  <>
                    {(canEditLeadAppointment || !(props.type === LEAD && isAppointment)) && (
                      <CustomButton
                        size="sm"
                        color="dark"
                        btnIcon={<FaPen size={14} />}
                        onClick={() =>
                          props.editComp({
                            ...props.item.event,
                            csm:
                              props.item.event.csm &&
                              !props.item.event.csm.is_active
                                ? null
                                : props.item.event.csm,
                            inActiveCsm:
                              props.item.event.csm &&
                              props.item.event.csm.is_active
                                ? null
                                : props.item.event.csm,
                          })
                        }
                        className="popover-icon-btn btn-icon"
                      />
                    )}
                    {isAppointment &&
                      [PROSPECT, LEAD, REALTOR].includes(props.type) &&
                      !isAppointmentTimePassed && (
                        <OverlayTrigger
                          placement="top"
                          overlay={<Tooltip>Resend Confirmation Email</Tooltip>}
                        >
                          <span>
                            <CustomButton
                              size="sm"
                              color="dark"
                              btnIcon={<BsEnvelopeArrowUpFill size={14} />}
                              onClick={handleResendConfirmationEmail}
                              className="popover-icon-btn btn-icon"
                            />
                          </span>
                        </OverlayTrigger>
                      )}
                    <CustomButton
                      size="sm"
                      color="dark"
                      btnIcon={<FaTrash size={14} />}
                      onClick={() => handleDeleteClick(props.item.event)}
                      className="popover-icon-btn btn-icon"
                    />
                  </>
                )}
            </div>
          ) : props.item.event.__typename === "CustomerFormSubmission" ? (
            <div className="webform-response-medium">
              <ButtonToolbar
                className="d-flex"
                style={{ alignItems: "center" }}
              >
                <CustomButton
                  color="dark"
                  disabled={true}
                  btnIcon={
                    <FaSms
                      size={14}
                      style={{
                        color: props.item.event.response_sms && "#80b602",
                      }}
                    />
                  }
                  className="icon-btn btn-icon"
                  style={{ background: "none", pointerEvents: "none" }}
                />
                <CustomButton
                  color="dark"
                  disabled={true}
                  btnIcon={
                    <FaPhone
                      size={14}
                      style={{
                        color: props.item.event.response_phone && "#80b602",
                      }}
                    />
                  }
                  className="icon-btn btn-icon b-none"
                  style={{ background: "none", pointerEvents: "none" }}
                />
                <CustomButton
                  color="dark"
                  disabled={true}
                  btnIcon={
                    <FaEnvelope
                      size={14}
                      style={{
                        color: props.item.event.response_email && "#80b602",
                      }}
                    />
                  }
                  className="icon-btn btn-icon"
                  style={{ background: "none", pointerEvents: "none" }}
                />
              </ButtonToolbar>
            </div>
          ) : props.item.event.__typename === "Email" ? (
            <CustomButton
              color="dark"
              onClick={() => {
                setState({ ...state, showEmailPreview: true });
              }}
              btnIcon={
                <FaClone
                  size={14}
                  style={{
                    color: "#80b602",
                  }}
                />
              }
              className="icon-btn btn-icon b-none"
            />
          ) : (
            ""
          )}
        </div>
      </div>

      {props.item.event.__typename === "CustomerFormSubmission" ? (
        <ul className="list-unstyled event-details-list">
          <li>
            <label>Received at:</label>
            <span>{formatActivityDate(props.item.event.created_at)}</span>
          </li>
          <li>
            <label>Form Type:</label>
            <span>{props.item.event.form_type || "-"}</span>
          </li>
          <li>
            <label>Division:</label>
            <span>{props.item.event.division?.name || "-"}</span>
          </li>
          <li>
            <label>Community:</label>
            <span>
              {props.item.event.community?.is_active === false && (
                <FaBan className="td-warning" size={10} />
              )}
              {props.item.event.community?.name || "-"}
            </span>
          </li>
          <li>
            <label>URL:</label>
            {props.item.event.url ? (
              <a
                target="_blank"
                href={addHttpToUrl(props.item.event.url)}
                rel="noopener noreferrer"
              >
                {props.item.event.url}
              </a>
            ) : (
              <span>{"-"}</span>
            )}
          </li>
          <li>
            <label>Details:</label>
            <span>{splitIntoFields(props.item.event.interested_in)}</span>
          </li>
          <li>
            <label>Other Data:</label>
            <span>{splitIntoFields(props.item.event.other_data)}</span>
          </li>
          <li>
            <label>Responded by:</label>
            <span>
              {props.item.event.responder
                ? `${props.item.event.responder.first_name} ${props.item.event.responder.last_name}`
                : "-"}
              {props.item.event.responded_at &&
                ` at ${formatActivityDate(props.item.event.responded_at)}`}
            </span>
          </li>
        </ul>
      ) : props.item.event.__typename === "Email" ? (
        <ul className="list-unstyled event-details-list">
          <li>
            <label>Subject</label>
            <span>{props.item.event.subject}</span>
          </li>
          <li>
            <label>Date</label>
            <span>
              <FaCalendarDay /> {newDate}
            </span>
          </li>
          <li>
            <label>Type</label>
            <span style={{ textTransform: "capitalize" }}>
              {props.item.event.email_type}
            </span>
          </li>
          <li>
            <label>BY:</label>
            <span>{csmNames.join(", ")}</span>
          </li>
          <li style={{ flexDirection: "column" }}>
            <label>Message</label>
            <span className="email-message">
              {truncate(props.item.event.body_preview, 254)}
            </span>
          </li>
          {props.item.event?.hasAttachments && (
            <li>
              <FaPaperclip /> Attachments
            </li>
          )}
        </ul>
      ) : props.item.event.__typename === "MailchimpEmailType" ? (
        <ul className="list-unstyled event-details-list">
          <li>
            <label>Subject</label>
            <span>{props.item.event.subject}</span>
          </li>
          <li>
            <label>Content URL</label>
            <a
              href={props.item.event.content_url}
              target="_blank"
              rel="noopener noreferrer"
            >
              {props.item.event.content_url}
            </a>
          </li>
          <li>
            <label>Address</label>
            <span>{props.item.event.email_addresses.toString()}</span>
          </li>
          <li>
            <label>From</label>
            <span>{props.item.event.email_from}</span>
          </li>
          <label>Date</label>
          <span>
            <FaCalendarDay /> {newDate}
          </span>
        </ul>
      ) : (
        <ul className="list-unstyled event-details-list">
          {isPhoneCallOrText ? (
            <>
              <li>
                <label>
                  {props.item.event.call_type?.id < 4
                    ? "Call Type"
                    : "Text Type"}
                </label>
                <span>{props.item.event.call_type?.name || "NA"}</span>
              </li>
              <li>
                <label>Sent By</label>
                <span>
                  {props.item.event.creator &&
                    props.item.event.creator.first_name +
                      " " +
                      props.item.event.creator.last_name}
                </span>
              </li>
            </>
          ) : (
            <>
              {props.item?.event?.isDashboardCalendar ? (
                <li>
                  <label>Name</label>
                  <span
                    onClick={handleNameClick}
                    style={{ cursor: "pointer", textDecoration: "underline" }}
                  >
                    {props.item?.event?.first_name_1 || ""}{" "}
                    {props.item?.event?.last_name_1 || ""}
                  </span>
                </li>
              ) : (
                ""
              )}
              <li>
                <label>Title</label>
                <span>{props.item.event.title}</span>
              </li>
            </>
          )}
          {isAppointment && (
            <>
              <li>
                <label>Type</label>
                <span>
                  {props.item.event.Appointment_Type &&
                    props.item.event.Appointment_Type.name}
                </span>
                {props.item.event.zoom_meeting_link && (
                  <span>
                    <FaVideo
                      size={14}
                      className="video-icon"
                      onClick={() => {
                        window.open(props.item.event.zoom_meeting_link);
                      }}
                    />
                  </span>
                )}
              </li>
              <li>
                <label>Purpose</label>
                <span>{AppointmentPurpose[props.item?.event?.appointment_purpose] || ""}</span>
              </li>
            </>
          )}
          {isFloorPlanActivity ? (
            <>
              <li>
                <label>Plan</label>
                <span>{props.item.event.plan_name}</span>
              </li>
              <li>
                <label>URL</label>
                <span>
                  {floorPlanUrl ? (
                    <a
                      href={floorPlanUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {floorPlanUrl}
                    </a>
                  ) : (
                    "———"
                  )}
                </span>
              </li>
              {props.item.event.community?.name && (
                <li>
                  <label>Community</label>
                  <span>{props.item.event.community.name}</span>
                </li>
              )}
            </>
          ) : null}
          {isPhoneCallOrText || isFollowUp || isTraffic ? (
            <li>
              <label>Details</label>
              <span>
                {props.item.event.attachments?.length
                  ? props.item.event.attachments.map(() => (
                      <FaPaperclip size={11} sx={{ ml: 5 }} />
                    ))
                  : null}
                {props.item.event.call_details || props.item.event.details || '-'}
              </span>
            </li>
          ) : null}
          {props.item.event.__typename === "PhoneCall" ? (
            <li>
              <label>Duration</label>
              <span>
                {props.item.event.call_duration > 0 ? (
                  <>
                    <FaClock />{" "}
                    {formatCallDurationsWithSeconds(
                      props.item.event.call_duration
                    )}
                  </>
                ) : (
                  "No Answer"
                )}
              </span>
            </li>
          ) : null}
          {isAppointment || isFollowUp ? (
            <li>
              <div style={{ fontSize: "12px" }}>
                <div className="d-flex flex-row">
                  <label>ASSIGNED TO</label>{" "}
                  {InactivityIcon(
                    props.item.event.csm,
                    props.item.event.csm &&
                      `${props.item.event.csm.first_name} ${props.item.event.csm.last_name}`
                  )}
                </div>
                {isPhoneCallOrText || isAppointment || isFollowUp ? (
                  <>
                    <label>COMMUNITY</label>
                    <span>{`${
                      props.item?.event?.community?.name ||
                      props.data?.community?.name ||
                      ""
                    }`}</span>
                  </>
                ) : null}
              </div>
            </li>
          ) : null}
          <li>
            <label>Date</label>
            <span>
              <FaCalendarDay /> {newDate}
            </span>
          </li>
          {isAppointment || isFollowUp ? (
            <li>
              <label>Set By</label>
              <span>
                {props.item.event.creator &&
                  `${props.item.event.creator.first_name} ${
                    props.item.event.creator.last_name
                  } 
                    at ${formatActivityDate(props.item.event.created_at)}`}
              </span>
            </li>
          ) : null}
          {props.item.event.__typename === "PhoneCall" &&
            props.item.event.recording && (
              <li>
                <label>Recording</label>
                <audio controls src={state.recordingUrl}>
                  Your browser does not support the
                  <code>audio</code> element.
                </audio>
              </li>
            )}
          {isTraffic ? (
            <>
              <li>
                <label>Community</label>
                <span>{props.item.event.community?.name || "-"}</span>
              </li>
              <li>
                <label>Source</label>
                <span>{props.item.event.source || "-"}</span>
              </li>
            </>
          ) : null}
        </ul>
      )}
    </div>
  );
};

export default compose(
  graphql(DELETE_PROSPECT_FOLLOW_UP, { name: "deleteProspectFollowUp" }),
  graphql(MARK_PROSPECT_FOLLOW_UP_DONE, { name: "markDoneProspectFollowUp" }),
  graphql(DELETE_PROSPECT_APPOINTMENT, { name: "deleteProspectAppointment" }),
  graphql(RESEND_PROSPECT_APPOINTMENT_CONFIRMATION, { name: "resendProspectAppointmentConfirmation" }),
  graphql(MARK_PROSPECT_APPOINTMENT_DONE, { name: "markDoneProspectAppointment" }),
  graphql(DELETE_PROSPECT_PHONE_CALL, { name: "deleteProspectPhoneCall" }),
  graphql(DELETE_PROSPECT_TRAFFIC, { name: "deleteProspectTraffic" }),

  graphql(MARK_LEAD_APPOINTMENT_DONE, { name: "markDoneLeadAppointment" }),
  graphql(DELETE_LEAD_APPOINTMENT, { name: "deleteLeadAppointment" }),
  graphql(RESEND_LEAD_APPOINTMENT_CONFIRMATION, { name: "resendLeadAppointmentConfirmation" }),
  graphql(MARK_LEAD_FOLLOW_UP_DONE, { name: "markDoneLeadFollowUp" }),
  graphql(DELETE_LEAD_FOLLOW_UP, { name: "deleteLeadFollowUp" }),
  graphql(DELETE_LEAD_PHONE_CALL, { name: "deleteLeadPhoneCall" }),
  graphql(DELETE_LEAD_TRAFFIC, { name: "deleteLeadTraffic" }),

  graphql(DELETE_HOMEOWNER_FOLLOW_UP, { name: "deleteHomeownerFollowUp" }),
  graphql(MARK_HOMEOWNER_FOLLOW_UP_DONE, { name: "markDoneHomeownerFollowUp" }),
  graphql(DELETE_HOMEOWNER_APPOINTMENT, { name: "deleteHomeownerAppointment" }),
  graphql(MARK_HOMEOWNER_APPOINTMENT_DONE, { name: "markDoneHomeownerAppointment" }),
  graphql(DELETE_HOMEOWNER_PHONE_CALL, { name: "deleteHomeownerPhoneCall" }),
  graphql(DELETE_HOMEOWNER_TRAFFIC, { name: "deleteHomeownerTraffic" }),

  graphql(DELETE_REALTOR_APPOINTMENT, { name: "deleteRealtorAppointment" }),
  graphql(RESEND_REALTOR_APPOINTMENT_CONFIRMATION, { name: "resendRealtorAppointmentConfirmation" }),
  graphql(DELETE_REALTOR_FOLLOW_UP, { name: "deleteRealtorFollowUp" }),
  graphql(MARK_REALTOR_APPOINTMENT_DONE, { name: "markDoneRealtorAppointment" }),
  graphql(MARK_REALTOR_FOLLOWUP_DONE, { name: "markDoneRealtorFollowup" })
)(Popover);
